import {ModalIds} from "@common/models/enums/modal-ids.enum";
import AppModal from "../../core/components/app-modal";
import {ArticleDetails} from "../../core/models/interfaces/article-details.interface";
import {ReactComponent as ChevronLeftIcon} from "@assets/icons/chevron-left.svg";
import {ReactComponent as PrintIcon} from "@assets/icons/print.svg";
import {ReactComponent as CrossIcon} from "@assets/icons/cross.svg";
import {ReactComponent as PencilIcon} from "@assets/icons/pencil3.svg";
import useModalWithData from "@common/hooks/use-modal-with-data";
import {addAnchorsToContent, generateTableOfContents} from "../helpers/generate-table-of-contents";
import React, {useMemo} from "react";
import AppButton from "@common/components/app-button";
import ArticleTag from "./ui/article-tag";
import {sanitizeHTML} from "../helpers/safe-html-renderer";
import {convertLexicalJsonToHtml} from "../helpers/lexical-to-html";
import {useNavigate} from "react-router-dom";
import {animated} from "@react-spring/web";
import {useModalAnimation} from "../../core/animations";
import {ArticleToPrintModelData} from "./article-to-print-modal";
import {useLoading} from "../../core/hooks/use-loading.hook";
import {classNames} from "@utils/class-names";
import SpinnerLoader from "@common/components/spinner-loader";
import SpecialistProfileImage from "../../specialists/components/specialist-profile-image";
import {ChevronRightIcon} from "@heroicons/react/24/solid";
import {convertLexicalJsonToPlainText} from "../helpers/lexical-to-plain-text";


interface PreviewArticleModalProps {
    titleBackButton?: string;
    showEditAction?: boolean;
}

export interface PreviewArticleModalData {
    articleData?: ArticleDetails;
}

const PreviewArticleModal = (props: PreviewArticleModalProps) => {
    const {
        titleBackButton = "Wróć",
        showEditAction = false,
    } = props
    const navigate = useNavigate();
    const {data, hideModal, isOpenModal} = useModalWithData<PreviewArticleModalData>(ModalIds.PREVIEW_ARTICLE);
    const {showModal, hideModal: hidePrintModal} = useModalWithData<ArticleToPrintModelData>(ModalIds.PRINT_ARTICLE);
    const article: ArticleDetails = data?.articleData!;
    const {isLoading, startLoading} = useLoading();

    const {springProps, closeModal} = useModalAnimation(!!isOpenModal(), 'medium');

    const handleCloseModal = () => {
        closeModal(() => {
            hidePrintModal();
            hideModal()
        });
    };

    const handleEdit = () => {
        closeModal( () => {
            hideModal();
            navigate(`/app/articles/edit/${article.id}`);
        },"fade-left");
    };

    const handlePrint = () => {
        startLoading("articleToPrintPrepare");
        showModal({articleData: article});
    };

    const handleImageLoading = (container: HTMLElement): void => {
        const images = container.querySelectorAll('img');
        images.forEach((img: HTMLImageElement) => {
            const parentWrapper = img.closest('.image-node') as HTMLElement;

            if (img.complete && img.naturalHeight !== 0) {
                img.classList.remove('loading');
                if (parentWrapper) {
                    parentWrapper.classList.remove('loading');
                }
            } else {
                img.classList.add('loading');
                if (parentWrapper) {
                    parentWrapper.classList.add('loading');
                }

                img.onload = () => {
                    img.classList.remove('loading');
                    if (parentWrapper) {
                        parentWrapper.classList.remove('loading');
                    }
                };

                img.onerror = () => {
                    img.classList.remove('loading');
                    img.classList.add('error');
                    if (parentWrapper) {
                        parentWrapper.classList.remove('loading');
                        parentWrapper.classList.add('error');
                    }
                };
            }
        });
    };

    const tableOfContents = useMemo(() => {
        if (!article?.content) return [];

        let htmlContent = article.content;
        try {
            if (article.content.startsWith('{') || article.content.startsWith('[')) {
                htmlContent = convertLexicalJsonToHtml(article.content);
            }
        } catch (error) {
            console.warn('Error converting Lexical JSON to HTML for TOC:', error);
        }

        return generateTableOfContents(htmlContent);
    }, [article?.content]);

    const safeTitle: string = useMemo(() => {
        if (!article?.title) return '';

        let htmlTitle = article.title;
        try {
            if (article.title.startsWith('{') || article.title.startsWith('[')) {
                htmlTitle = convertLexicalJsonToHtml(article.title);
            }
        } catch (error) {
            console.warn('Error converting title Lexical JSON to HTML:', error);
        }

        return sanitizeHTML(htmlTitle);
    }, [article?.title]);

    const safeSummary: string = useMemo(() => {
        if (!article?.summary) return '';

        let htmlSummary = article.summary;
        try {
            if (article.summary.startsWith('{') || article.summary.startsWith('[')) {
                htmlSummary = convertLexicalJsonToHtml(article.summary);
            }
        } catch (error) {
            console.warn('Error converting summary Lexical JSON to HTML:', error);
        }

        return sanitizeHTML(htmlSummary);
    }, [article?.summary]);

    const safeContent: string = useMemo(() => {
        if (!article?.content) return '';

        let htmlContent = article.content;
        try {
            if (article.content.startsWith('{') || article.content.startsWith('[')) {
                htmlContent = convertLexicalJsonToHtml(article.content);
            }
        } catch (error) {
            console.warn('Error converting Lexical JSON to HTML:', error);
        }

        const contentWithAnchors = addAnchorsToContent(htmlContent);
        return sanitizeHTML(contentWithAnchors);
    }, [article?.content]);

    if (!article) return null;

    return (
        <>
            <AppModal
                id={ModalIds.PREVIEW_ARTICLE}
                key={ModalIds.PREVIEW_ARTICLE}
                zIndex={40}
                forceFixed={true}
                classModal="max-w-4xl flex justify-center">
                <animated.div
                    style={springProps}
                    className="h-full flex flex-col bg-surface-level-1 rounded-2xl max-h-[85svh] md:max-h-[80vh] min-w-full md:min-w-[640px] max-w-[640px]"
                >
                    <div className="flex items-center justify-between p-4 border-b border-gray-200 max-h-[72px]">
                        <div className="flex items-center gap-3">
                            <AppButton
                                onClick={() => handleCloseModal()}
                                className="border-0 rounded-full !p-4 min-w-[48px] text-gray-600 hover:text-gray-800 hover:bg-on-surface/5 transition-colors"
                            >
                                <ChevronLeftIcon className=""/>
                            </AppButton>
                            <span className="text-lg font-semibold">{titleBackButton}</span>
                        </div>
                        <div className="flex items-center">
                            <AppButton
                                onClick={handlePrint}
                                classNameContentWrapper="flex-row-reverse"
                                className={classNames(
                                    "border-0 rounded-full !p-4 text-gray-600 hover:text-gray-800 hover:bg-on-surface/5 transition-colors",
                                    isLoading("articleToPrintPrepare") && "!bg-on-surface/5"
                                )}
                            >
                                <div className="flex gap-2 items-center">
                                    <PrintIcon className="w-4 h-4"/>
                                    {isLoading("articleToPrintPrepare") && <SpinnerLoader color="#333"/>}
                                </div>
                            </AppButton>
                            {showEditAction ? (
                                <AppButton
                                    onClick={handleEdit}
                                    className="border-0 rounded-full !p-4 text-gray-600 hover:text-gray-800 hover:bg-on-surface/5 transition-colors"
                                >
                                    <PencilIcon className="w-[15px] h-[15px]"/>
                                </AppButton>
                            ) : (
                                <AppButton
                                    onClick={() => handleCloseModal()}
                                    className="border-0 rounded-full !p-4 text-gray-600 hover:text-gray-800 hover:bg-on-surface/5 transition-colors"
                                >
                                    <CrossIcon className="w-[15px] h-[15px]"/>
                                </AppButton>
                            )}
                        </div>
                    </div>

                    <div
                        className="px-8 pt-8 pb-6 flex flex-col gap-6 overflow-y-auto scroll-smooth preview-article relative">
                        <div className="inline-block align-middle text-sm text-on-surface/60 w-fit">
                            {article?.tags?.map((tag, index) => (
                                <React.Fragment key={index}>
                                    <ArticleTag text={tag}/>
                                    {index < article?.tags.length - 1 && <span className="mx-2">·</span>}
                                </React.Fragment>
                            ))}
                        </div>

                        <div className="flex-1 flex flex-col gap-6">
                            <h1
                                className="!text-black"
                                dangerouslySetInnerHTML={{__html: safeTitle}}/>

                            <p
                                className="!text-[#334155]"
                                dangerouslySetInnerHTML={{__html: safeSummary}}/>

                            {tableOfContents.length > 0 && (
                                <ul className="space-y-2 text-link ml-5">
                                    {tableOfContents.map((item, index) => (
                                        <li key={index} className="flex items-center font-semibold text-base">
                                            <div className="w-1 h-1 bg-link rounded-full mr-3 flex-shrink-0"></div>
                                            <a
                                                href={`#${item.id}`}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    const element = document.getElementById(item.id);
                                                    if (element) {
                                                        element.scrollIntoView({
                                                            behavior: 'smooth',
                                                            block: 'start',
                                                            inline: 'nearest'
                                                        });
                                                    }
                                                }}
                                                className="cursor-pointer hover:text-link-hover transition-colors duration-200"
                                            >
                                                {item.title}
                                            </a>
                                        </li>
                                    ))}
                                </ul>
                            )}

                            <div
                                className="[&>*]:!text-[#334155]"
                                dangerouslySetInnerHTML={{__html: safeContent}}
                                ref={(el) => {
                                    el && handleImageLoading(el)
                                }}
                            />

                            {article.exercises && article.exercises.length > 0 && (
                                <section
                                    className="[&>*]:!text-[#334155] space-y-1 border-t border-outline-mild border-solid pt-8">
                                    <h2 className="rich-text-h2">
                                        Rekomendowane ćwiczenia
                                    </h2>
                                    <p className="text-on-surface/70">
                                        Zestaw ćwiczeń przedstawiony poniżej został przygotowany jako praktyczne
                                        rozszerzenie treści artykułu. Ich zastosowanie powinno być każdorazowo
                                        dostosowane do aktualnego stanu zdrowia i zaleceń prowadzącego specjalisty.
                                    </p>
                                    <ul className="list-disc pl-5 space-y-1">
                                        {article.exercises.map((exercise) => (
                                            <li key={exercise.id}>
                                                <strong>{exercise.title}</strong>
                                                {exercise.description && (
                                                    <span>{`: ${exercise.description}`}</span>
                                                )}
                                                {exercise.url && (
                                                    <span>
                                                        {" "}
                                                        <a
                                                            href={exercise.url}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="text-link hover:text-link-hover"
                                                        >
                                                            Zobacz ćwiczenie
                                                        </a>
                                                    </span>
                                                )}
                                            </li>
                                        ))}
                                    </ul>
                                </section>
                            )}

                            {article?.author && (
                                <section
                                    className="[&>*]:!text-[#334155] border-t border-outline-mild border-solid flex flex-col gap-4 pt-6">
                                    <h2 className="rich-text-h2">
                                        O autorze
                                    </h2>
                                    <div className="flex items-center gap-4">
                                        <div className="shrink-0">
                                            <SpecialistProfileImage
                                                specialistId={article.author.id}
                                                specialistName={article.author.fullName}
                                                className="w-16 h-16 !rounded-2xl"
                                                classNameImg="!rounded-2xl"
                                            />
                                        </div>
                                        <div className="flex flex-col gap-1">
                                            <h3 className="font-semibold text-lg text-[#161C1D] m-0 leading-6">
                                                {article.author.fullName}
                                            </h3>
                                            {article.author.specialization && (
                                                <p className="text-[#161C1D] text-base m-0 leading-5">
                                                    {article.author.specialization}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </section>

                            )}

                            {article?.relatedArticles && article.relatedArticles.length > 0 && (
                                <section
                                    className="[&>*]:!text-[#334155] border-t border-outline-mild border-solid flex flex-col gap-4 pt-6">
                                    <h2 className="rich-text-h2">
                                        Powiązane artykuły
                                    </h2>
                                    <ul className="flex flex-col gap-2 list-none p-0 m-0">
                                        {article.relatedArticles.map((related) => (
                                            <li key={related.id}>
                                                <AppButton
                                                    classNameContentWrapper="[&>div]:w-full"
                                                    className="w-full px-[14px] py-3 bg-white border border-outline-mild rounded-lg text-left hover:border-outline transition-colors duration-150 group">
                                                    <div className="flex items-center justify-between w-full min-w-full">
                                                        <span className="text-sm text-on-surface leading-snug truncate">
                                                            {convertLexicalJsonToPlainText(related.title)}
                                                        </span>
                                                        <ChevronRightIcon className="w-3.5 h-3.5 text-on-surface/30 flex-shrink-0 group-hover:text-on-surface/60 transition-colors"/>
                                                    </div>
                                                </AppButton>
                                            </li>
                                        ))}
                                    </ul>
                                </section>
                            )}
                        </div>
                    </div>

                    <div
                        className="flex items-center flex-wrap md:flex-nowrap justify-end gap-2 py-3 md:py-0 md:h-[72px] md:min-h-[72px] rounded-b-2xl border-t border-outline-mild px-8">
                        <AppButton
                            onClick={() => handleCloseModal()}
                            className="bg-transparent hover:bg-on-surface/5 w-full md:w-fit max-h-[40px] h-[40px] !px-6 !pt-2 border-[2px] !border-on-surface font-semibold text-base !rounded-full cursor-auto"
                            label="Zamknij"
                        />
                        <AppButton
                            className="bg-primary w-full md:w-fit max-h-[40px] h-[40px] !px-6 !pt-2 text-on-primary font-semibold text-base !rounded-full cursor-auto"
                            label="Umów się do specjalisty"
                        />
                    </div>
                </animated.div>
            </AppModal>
        </>
    )
}

export default PreviewArticleModal
